import moment from 'moment';
import { GOALS } from 'src/pages/constants';

type StrategyType = 'aggressive' | 'sustainable' | 'gradual';

interface GoalDurationInput {
  currentWeight: number;
  targetWeight: number;
  goal: number; // 2, 3, or 4
  strategy?: StrategyType;
}

export function getGoalSummary({
  currentWeight,
  targetWeight,
  goal,
  strategy = 'sustainable',
}: GoalDurationInput): {
  targetWeight: number;
  rateString: string;
  estimatedCompletion: string; // e.g. "20 March 2026"
} {
  // Map numeric goal → text
  const goalMap: Record<number, 'maintain' | 'musclegain' | 'fatloss'> = {
    [GOALS.maintain]: 'maintain',
    [GOALS.muscle_gain]: 'musclegain',
    [GOALS.fat_loss]: 'fatloss',
  };

  const goalText = goalMap[goal];
  if (!goalText) {
    return { targetWeight, rateString: '', estimatedCompletion: '' };
  }

  // Rules (grams/week)
  const rules: Record<
    'fatloss' | 'musclegain' | 'maintain',
    Record<StrategyType, [number, number] | null>
  > = {
    fatloss: {
      aggressive: [400, 900],
      sustainable: [300, 800],
      gradual: [200, 500],
    },
    musclegain: {
      gradual: [50, 150],
      sustainable: [100, 250],
      aggressive: [200, 400],
    },
    maintain: {
      aggressive: null,
      sustainable: null,
      gradual: null,
    },
  };

  // Handle maintenance goal
  if (goalText === 'maintain') {
    return {
      targetWeight,
      rateString: '200g',
      estimatedCompletion: 'You’re maintaining — no target date.',
    };
  }

  const rateRange = rules[goalText][strategy];
  if (!rateRange) {
    return { targetWeight, rateString: '', estimatedCompletion: '' };
  }

  const [minRate, maxRate] = rateRange;
  const diffKg = Math.abs(targetWeight - currentWeight);
  const minRateKg = minRate / 1000;

  // Use the *slower* rate → max weeks (higher side)
  const maxWeeks = Math.ceil(diffKg / minRateKg);

  // Calculate estimated completion date (from today)
  const estimatedCompletion = moment()
    .add(maxWeeks, 'weeks')
    .format('D MMMM YYYY');

  // Weekly rate string
  const rateString =
    minRate === maxRate ? `${minRate}g` : `${minRate}-${maxRate}g`;

  return {
    targetWeight,
    rateString,
    estimatedCompletion,
  };
}
